import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { fmtCurrency, fmtDate, STATUS_TR } from "@/utils/format";
import { ArrowLeft, Mail, Phone, MapPin, Hash, Calendar, Eye, ShoppingCart } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import Pagination from "@/components/Pagination";
import OrderDetailsDialog from "@/components/OrderDetailsDialog";

const STATUS_COLORS = {
  in_progress: "bg-yellow-100 text-yellow-800 border-yellow-800",
  completed: "bg-green-100 text-green-800 border-green-800",
  shipped: "bg-[#00A650] text-white border-[#00A650]",
  cancelled: "bg-[#FF3B30] text-white border-[#FF3B30]",
  partially_cancelled: "bg-orange-100 text-orange-800 border-orange-800",
};

const PAGE_SIZE = 10;

function Stat({ label, value, sub, testid, tone }) {
  const toneCls = tone === "primary" ? "border-l-[#0055FF]" : tone === "success" ? "border-l-[#00A650]" : "border-l-[#09090B]";
  return (
    <div className={`bg-white border-2 border-[#09090B] border-l-[6px] ${toneCls} p-4`} data-testid={testid}>
      <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-500">{label}</div>
      <div className="mt-1 text-2xl font-mono tracking-tight font-bold">{value}</div>
      {sub && <div className="mt-1 text-[10px] font-mono text-neutral-500">{sub}</div>}
    </div>
  );
}

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPrivileged = user?.role === "admin" || user?.role === "sub_admin";
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [detailId, setDetailId] = useState(null);

  const load = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get(`/customers/${id}/detail?_ts=${Date.now()}`);
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setRefreshing(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.orders.filter((o) => {
      if (status !== "all" && o.status !== status) return false;
      if (q) {
        const ql = q.toLocaleLowerCase("tr-TR");
        const hit =
          (o.order_number || "").toLocaleLowerCase("tr-TR").includes(ql) ||
          (o.quote_number || "").toLocaleLowerCase("tr-TR").includes(ql);
        if (!hit) return false;
      }
      return true;
    });
  }, [data, q, status]);

  useEffect(() => { setPage(1); }, [q, status]);
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  if (!data) {
    return <div className="p-8 text-sm font-mono text-neutral-500">Yükleniyor…</div>;
  }

  const { customer, stats } = data;

  return (
    <div className="space-y-6" data-testid="customer-detail-page">
      <PageHeader
        kicker="Müşteri Kartı"
        title={customer.name}
        subtitle={customer.tax_id ? `Vergi No: ${customer.tax_id}` : undefined}
        onRefresh={load}
        refreshing={refreshing}
        actions={
          <Button
            variant="outline"
            className="rounded-none border-2 border-[#09090B]"
            onClick={() => navigate("/musteriler")}
            data-testid="customer-detail-back"
          >
            <ArrowLeft size={16} className="mr-2" /> Müşteri Listesi
          </Button>
        }
      />

      {/* Customer info */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-white border-2 border-[#09090B] p-4">
          <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-1 flex items-center gap-1">
            <Mail size={12} /> E-posta
          </div>
          <div className="font-mono text-sm break-all">{customer.email || <span className="text-neutral-400 italic">yok</span>}</div>
        </div>
        <div className="bg-white border-2 border-[#09090B] p-4">
          <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-1 flex items-center gap-1">
            <Phone size={12} /> Telefon
          </div>
          <div className="font-mono text-sm">{customer.phone || <span className="text-neutral-400 italic">yok</span>}</div>
        </div>
        <div className="bg-white border-2 border-[#09090B] p-4">
          <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-1 flex items-center gap-1">
            <Hash size={12} /> Vergi No
          </div>
          <div className="font-mono text-sm">{customer.tax_id || <span className="text-neutral-400 italic">yok</span>}</div>
        </div>
        <div className="bg-white border-2 border-[#09090B] p-4">
          <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-1 flex items-center gap-1">
            <MapPin size={12} /> Adres
          </div>
          <div className="font-mono text-sm whitespace-pre-line">{customer.address || <span className="text-neutral-400 italic">yok</span>}</div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat
          label="Toplam Ciro"
          value={fmtCurrency(stats.total_revenue)}
          testid="customer-stat-revenue"
          tone="primary"
          sub={isPrivileged
            ? `KDV Hariç: ${fmtCurrency(stats.total_subtotal)} · KDV: ${fmtCurrency(stats.total_tax)}`
            : `KDV Dahil`}
        />
        <Stat
          label="Sipariş Sayısı"
          value={stats.order_count}
          testid="customer-stat-orders"
          tone="success"
          sub={stats.cancelled_count > 0 ? `${stats.cancelled_count} iptal` : undefined}
        />
        <Stat
          label="İlk Sipariş"
          value={stats.first_order ? fmtDate(stats.first_order) : "—"}
          testid="customer-stat-first"
        />
        <Stat
          label="Son Sipariş"
          value={stats.last_order ? fmtDate(stats.last_order) : "—"}
          testid="customer-stat-last"
        />
      </div>

      {/* Filter bar */}
      <div className="bg-white border-2 border-[#09090B] p-4 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
        <div className="md:col-span-2">
          <label className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Sipariş No / Teklif No</label>
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="SIP-2026-… / TEK-2026-…"
            className="rounded-none border-2 border-[#09090B] focus-visible:ring-0"
            data-testid="customer-orders-search"
          />
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Durum</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            data-testid="customer-orders-status"
            className="w-full h-10 rounded-none border-2 border-[#09090B] font-mono text-sm px-2 bg-white focus:outline-none"
          >
            <option value="all">Tümü</option>
            <option value="in_progress">İşlemde</option>
            <option value="completed">Tamamlandı</option>
            <option value="shipped">Sevk Edildi</option>
            <option value="cancelled">İptal</option>
            <option value="partially_cancelled">Kısmi İptal</option>
          </select>
        </div>
        <div className="text-right text-xs font-mono text-neutral-500">
          {filtered.length} / {data.orders.length} sipariş
        </div>
      </div>

      {/* Orders */}
      <div className="bg-white border-2 border-[#09090B]" data-testid="customer-orders-table">
        <Table>
          <TableHeader>
            <TableRow className="bg-neutral-100 border-b-2 border-[#09090B]">
              <TableHead className="font-mono uppercase text-xs">Sipariş No</TableHead>
              <TableHead className="font-mono uppercase text-xs">Teklif No</TableHead>
              <TableHead className="font-mono uppercase text-xs">Tarih</TableHead>
              <TableHead className="font-mono uppercase text-xs">Durum</TableHead>
              <TableHead className="font-mono uppercase text-xs text-right">Toplam</TableHead>
              <TableHead className="font-mono uppercase text-xs text-right">İşlem</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paged.map((o) => (
              <TableRow key={o.id} data-testid={`customer-order-row-${o.id}`}>
                <TableCell className="font-mono font-bold text-sm">{o.order_number}</TableCell>
                <TableCell className="font-mono text-sm text-neutral-600">{o.quote_number}</TableCell>
                <TableCell className="font-mono text-sm flex items-center gap-1">
                  <Calendar size={12} /> {fmtDate(o.created_at)}
                </TableCell>
                <TableCell>
                  <span className={`inline-block px-2 py-0.5 text-[10px] font-mono uppercase border ${STATUS_COLORS[o.status] || "bg-neutral-100"}`}>
                    {STATUS_TR[o.status] || o.status}
                  </span>
                </TableCell>
                <TableCell className="text-right font-mono font-bold">{fmtCurrency(o.total)}</TableCell>
                <TableCell className="text-right">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setDetailId(o.id)}
                    data-testid={`customer-order-view-${o.id}`}
                    title="Sipariş detayı"
                  >
                    <Eye size={14} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-10 text-neutral-500">
                  <ShoppingCart size={20} className="mx-auto mb-2 opacity-40" />
                  {data.orders.length === 0 ? "Bu müşteri için sipariş yok" : "Filtreye uyan sipariş yok"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <Pagination page={page} total={filtered.length} pageSize={PAGE_SIZE} onChange={setPage} testidPrefix="customer-orders-pagination" />
      </div>

      <OrderDetailsDialog
        orderId={detailId}
        open={!!detailId}
        onOpenChange={(v) => !v && setDetailId(null)}
        canEditDueDate={false}
      />
    </div>
  );
}
